import { useCreateTelantAdmin, useGetTenant, useCreateTenantUser, useGetTenantRoles } from "api/tenant/service";
import React, { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    CustomInput,
    CardFooter,
} from "reactstrap";
import { formatPhoneNumber, validatePhoneNumber } from "utils/phoneUtils";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../contexts/NotificationContext";
import Select from "components/Select";
import Institution from "./Institution";
import SalesLocationsTab from "./components/SalesLocationsTab";
import BookkeepingTab from "./components/BookkeepingTab";
import InvestorTab from "./components/InvestorTab";
import PortfolioTab from "./components/PortfolioTab";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

const suffixOptions = ["", "Jr.", "Sr.", "I", "II", "III", "IV", "V"];
const salutationOptions = ["", "Mr.", "Mrs.", "Ms.", "Dr.", "Prof.", "Rev.", "Sir", "Madam"];
const genderOptions = ["", "Male", "Female", "Other", "Prefer not to say"];

const Stepper = ({ currentStep, steps, activeColorStyle }) => {
    const totalSteps = steps.length;
    const progress = (currentStep / (totalSteps - 1)) * 100;

    return (
        <div className="position-relative mb-1" style={{ minHeight: '80px' }}>
            {/* Progress Track Background */}
            <div
                className="position-absolute w-100 px-5"
                style={{ top: '20px', left: 0, zIndex: 0 }}
            >
                <div className="position-relative" style={{ margin: '0 20px', height: '2px' }}>
                    <div
                        className="position-absolute w-100"
                        style={{
                            height: '100%',
                            background: '#e9ecef',
                            borderRadius: '10px'
                        }}
                    />
                    <div
                        className="position-absolute h-100"
                        style={{
                            width: `${progress}%`,
                            background: activeColorStyle.borderColor || activeColorStyle.backgroundColor,
                            borderRadius: '10px',
                            transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: `0 0 10px ${(activeColorStyle.borderColor || activeColorStyle.backgroundColor)}44`
                        }}
                    />
                </div>
            </div>

            {/* Steps Container */}
            <div className="d-flex justify-content-between px-5 position-relative" style={{ zIndex: 1 }}>
                {steps.map((s, idx) => {
                    const isCompleted = idx < currentStep;
                    const isActive = idx === currentStep;
                    const color = activeColorStyle.borderColor || activeColorStyle.backgroundColor;

                    return (
                        <div
                            key={idx}
                            className="d-flex flex-column align-items-center"
                            style={{ width: '40px', position: 'relative' }}
                        >
                            {/* Circle */}
                            <div
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: isCompleted ? color : '#fff',
                                    color: isCompleted ? '#fff' : isActive ? color : '#adb5bd',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: '700',
                                    border: `2px solid ${isCompleted || isActive ? color : '#e9ecef'}`,
                                    transition: 'all 0.4s ease',
                                    boxShadow: isActive ? `0 0 15px ${color}44` : 'none',
                                    zIndex: 2,
                                    fontSize: '14px'
                                }}
                            >
                                {isCompleted ? (
                                    <i className="tim-icons icon-check-2" />
                                ) : (
                                    <span>{idx + 1}</span>
                                )}
                            </div>

                            {/* Label */}
                            <div
                                className="text-center"
                                style={{
                                    position: 'absolute',
                                    top: '48px',
                                    width: '120px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '11px',
                                    fontWeight: isActive ? '700' : '500',
                                    color: isActive ? '#344675' : '#8898aa',
                                    lineHeight: '1.2',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {s.title}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


const TenantAdd = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
    const { id } = useParams();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        isTheme2,
        inputStyle,
        inputClassName,
        selectStyles,
        theme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        activeColorStyle,
        pageKey: "tenant-add",
        insetClass: THEME2_ADMIN_INSETS.tenants,
        withSelectStyles: true,
    });

    const theme2FormProps = { isTheme2, inputStyle, inputClassName, selectStyles };

    const tenantAddTheme2FieldsCss = useMemo(
        () =>
            isTheme2
                ? `${theme2FieldsCss}
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-institution-card .card-header {
                    padding-bottom: 0 !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-institution-card .card-body {
                    padding-top: 8px !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-institution-tabs {
                    margin-bottom: 0 !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-institution-tab-content {
                    margin-top: 0 !important;
                    padding-top: 0 !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution .nav-link,
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content .nav-link {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content table th,
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content table td {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content .card-title,
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content .institution-inventory-card__title {
                    font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px) !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content .institution-inventory-card__description {
                    font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
                  }
                  .tenant-add-page.tenant-add-page--theme2 .tenant-add-form-fields .tenant-add-institution-tab-content .card .card-header {
                    font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px) !important;
                  }
                  body.white-content .tenant-add-page.tenant-add-page--theme2 .institution-inventory-card__title {
                    color: #3c4858 !important;
                  }
                  body:not(.white-content) .tenant-add-page.tenant-add-page--theme2 .institution-inventory-card__title {
                    color: #ffffff !important;
                  }
                  body:not(.white-content) .tenant-add-page.tenant-add-page--theme2 .institution-inventory-card__description {
                    color: rgba(255, 255, 255, 0.8) !important;
                  }`
                : theme2FieldsCss,
        [isTheme2, theme2FieldsCss]
    );

    const navigate = useNavigate();
    const { t } = useTranslation();

    // Mutations & Queries
    const createUserMutation = useCreateTelantAdmin();
    const [stepOverride, setStepOverride] = useState(null);
    const [fetchType, setFetchType] = useState(null);
    const [hideStepper, setHideStepper] = useState(window.innerWidth < 700);

    useEffect(() => {
        const handleResize = () => setHideStepper(window.innerWidth < 700);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { data: tenantData, isLoading: isLoadingTenant } = useGetTenant(id, fetchType);

    const currentStep = useMemo(() => {
        if (stepOverride !== null) return stepOverride;
        if (!tenantData) return 0;
        if (tenantData.step !== undefined) return tenantData.step === 0 ? 1 : tenantData.step;

        // Fallback mapping if step is missing from typed responses
        const stepMap = {
            admin: 1,
            institution: 2,
            sales_location: 3,
            bookkeeping_company: 4,
            investor: 5,
            portfolio: 6
        };
        return stepMap[fetchType] || 1;
    }, [stepOverride, tenantData, fetchType]);

    const handleNextStep = () => {
        setStepOverride(currentStep + 1);
    };

    const handlePrevStep = () => {
        if (currentStep > 0) {
            setStepOverride(currentStep - 1);
        }
    };

    useEffect(() => {
        if (currentStep === 1) {
            if (fetchType !== "admin") setFetchType("admin");
        } else if (currentStep === 2) {
            if (fetchType !== "institution") setFetchType("institution");
        } else if (currentStep === 3) {
            if (fetchType !== "sales_location") setFetchType("sales_location");
        } else if (currentStep === 4) {
            if (fetchType !== "bookkeeping_company") setFetchType("bookkeeping_company");
        } else if (currentStep === 5) {
            if (fetchType !== "investor") setFetchType("investor");
        } else if (currentStep === 6) {
            if (fetchType !== "portfolio") setFetchType("portfolio");
        } else if (currentStep === 0 || currentStep === 7) {
            if (fetchType !== null) setFetchType(null);
        }
    }, [currentStep, fetchType]);
    const { data: rolesData, isLoading: rolesLoading } = useGetTenantRoles(id);
    const { showNotification } = useNotification();

    // STEP 0 State
    const [formData, setFormData] = useState({
        email: '',
        alt_email: '',
        tenant_name: '',
        tenant_status: true,
        phone: '',
    });
    const [errors, setErrors] = useState({});

    // STEP 1 State
    const [userFormData, setUserFormData] = useState({
        email: "",
        alt_email: "",
        password: "",
        full_name: "",
        address: "",
        salutation: "",
        gender: "",
        suffix: "",
        title: "",
        work_phone: "",
        home_phone: "",
        cell_phone: "",
        fax: "",
    });
    const [userErrors, setUserErrors] = useState({});
    const [selectedRole, setSelectedRole] = useState("");

    // Sync form data if editing or in a later step
    useEffect(() => {
        if (tenantData) {
            setFormData({
                email: tenantData.email || '',
                alt_email: tenantData.alt_email || '',
                tenant_name: tenantData.name || '',
                tenant_status: tenantData.tenant_status === 1,
                phone: tenantData.phone || '',
            });

            if (tenantData.admin) {
                const admin = tenantData.admin;
                const details = admin.user_detail || {};
                setUserFormData(prev => ({
                    ...prev,
                    email: admin.email || '',
                    alt_email: details.alt_email || admin.alt_email || '',
                    full_name: details.full_name || '',
                    address: details.address || '',
                    salutation: details.salutation || '',
                    gender: details.gender || '',
                    suffix: details.suffix || '',
                    title: details.title || '',
                    work_phone: details.work_phone || '',
                    home_phone: details.home_phone || '',
                    cell_phone: details.cell_phone || '',
                    fax: details.fax || '',
                }));
            }
        }
    }, [tenantData]);

    const steps = [
        { title: 'Tenant Information', step: 0 },
        { title: 'Add New Admin', step: 1 },
        { title: 'Institution', step: 2 },
        { title: 'Sales Locations', step: 3 },
        { title: 'Bookkeeping Company', step: 4 },
        { title: 'Investor', step: 5 },
        { title: 'Portfolio', step: 6 },
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = type === 'checkbox' ? checked : value;

        if (['work_phone', 'home_phone', 'cell_phone', 'fax', 'phone'].includes(name)) {
            processedValue = formatPhoneNumber(value);
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleUserInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;
        if (name === 'work_phone' || name === 'home_phone' || name === 'cell_phone') {
            formattedValue = formatPhoneNumber(value);
        }

        setUserFormData((prev) => ({ ...prev, [name]: formattedValue }));
        if (userErrors[name]) {
            setUserErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.tenant_name.trim()) {
            newErrors.tenant_name = 'Tenant Name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateUserForm = () => {
        const newErrors = {};
        if (!userFormData.email) newErrors.email = t('userAdd.errors.emailRequired') || 'Email Required';
        if (!userFormData.full_name) newErrors.full_name = t('userAdd.errors.fullNameRequired') || 'Full Name Required';
        if (!userFormData.password) newErrors.password = t('userAdd.errors.passwordRequired') || 'Password Required';
        else if (userFormData.password.length < 6) newErrors.password = t('userAdd.errors.passwordMinLength') || 'Min 6 chars';

        const phoneError = validatePhoneNumber(userFormData.work_phone);
        if (phoneError) newErrors.work_phone = phoneError;

        setUserErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const data = {
            name: formData.tenant_name,
            email: formData.email,
            alt_email: formData.alt_email,
            phone: formData.phone,
            tenant_status: formData.tenant_status ? 1 : 0
        };

        const submitData = (id && id !== "undefined") ? { tenant: data, code: id } : data;

        try {
            await createUserMutation.mutateAsync(submitData);
            if (id) {
                showNotification('success', 'Tenant information updated successfully!');
                handleNextStep();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleUserSubmit = async (e) => {
        e.preventDefault();
        if (!validateUserForm()) return;

        const data = {
            ...userFormData,
        };

        const submitData = { user: data, code: id }

        try {
            await createUserMutation.mutateAsync(submitData);
            showNotification('success', 'Admin account updated successfully!');
            handleNextStep();
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handleCancel = () => {
        navigate('/tenant');
    };

    const renderStepContent = () => {
        if (currentStep === 0) {
            return (
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="email">Email *</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={inputClassName(!!errors.email)}
                                    style={inputStyle}
                                    invalid={!!errors.email}
                                    disabled={createUserMutation.isPending}
                                />
                                {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="alt_email">Alternative Email</Label>
                                <Input
                                    id="alt_email"
                                    name="alt_email"
                                    type="email"
                                    value={formData.alt_email}
                                    onChange={handleInputChange}
                                    className={inputClassName(false)}
                                    style={inputStyle}
                                    disabled={createUserMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="tenant_name">Tenant Name *</Label>
                                <Input
                                    id="tenant_name"
                                    name="tenant_name"
                                    type="text"
                                    value={formData.tenant_name}
                                    onChange={handleInputChange}
                                    className={inputClassName(!!errors.tenant_name)}
                                    style={inputStyle}
                                    invalid={!!errors.tenant_name}
                                    disabled={createUserMutation.isPending}
                                />
                                {errors.tenant_name && <div className="invalid-feedback d-block">{errors.tenant_name}</div>}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="phone">Phone</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className={inputClassName(false)}
                                    style={inputStyle}
                                    disabled={createUserMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <CustomInput
                                    type="switch"
                                    id="tenant_status"
                                    name="tenant_status"
                                    label="Active Status"
                                    checked={formData.tenant_status}
                                    onChange={handleInputChange}
                                    disabled={createUserMutation.isPending}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <Button
                                    type="submit"
                                    style={{ ...activeColorStyle, color: 'white' }}
                                    disabled={createUserMutation.isPending}
                                >
                                    {createUserMutation.isPending ? (
                                        <>
                                            <Spinner size="sm" className="mr-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        "Save & Continue"
                                    )}
                                </Button>
                                {(id && tenantData?.step > 0) && (
                                    <Button
                                        color="secondary"
                                        onClick={handleNextStep}
                                        disabled={!id || createUserMutation.isPending}
                                    >
                                        Next
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Form>
            );
        }

        if (currentStep === 1) {
            return (
                <Form onSubmit={handleUserSubmit}>
                    <Row>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.email') || 'Email'} <span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        className={inputClassName(!!userErrors.email)}
                                        style={inputStyle}
                                        name="email"
                                        type="email"
                                        value={userFormData.email}
                                        onChange={handleUserInputChange}
                                        invalid={!!userErrors.email}
                                        placeholder={t('userAdd.placeholders.email')}
                                        disabled={createUserMutation.isPending}
                                    />
                                    {userErrors.email && <div className="text-danger small">{userErrors.email}</div>}
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.password') || 'Password'} <span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        className={inputClassName(!!userErrors.password)}
                                        style={inputStyle}
                                        name="password"
                                        type="password"
                                        value={userFormData.password}
                                        onChange={handleUserInputChange}
                                        invalid={!!userErrors.password}
                                        placeholder={t('userAdd.placeholders.password')}
                                        disabled={createUserMutation.isPending}
                                    />
                                    {userErrors.password && <div className="text-danger small">{userErrors.password}</div>}
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.fullName') || 'Full Name'} <span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        className={inputClassName(!!userErrors.full_name)}
                                        style={inputStyle}
                                        name="full_name"
                                        value={userFormData.full_name}
                                        onChange={handleUserInputChange}
                                        invalid={!!userErrors.full_name}
                                        placeholder={t('userAdd.placeholders.fullName')}
                                        disabled={createUserMutation.isPending}
                                    />
                                    {userErrors.full_name && <div className="text-danger small">{userErrors.full_name}</div>}
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.title') || 'Title'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="title"
                                        value={userFormData.title}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.title')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.address') || 'Address'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="address"
                                        value={userFormData.address}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.address')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.salutation') || 'Salutation'}</Label>
                                    <Select
                                        activeColor={activeColor}
                                        className="react-select"
                                        styles={selectStyles}
                                        name="salutation"
                                        value={salutationOptions.find(opt => opt === userFormData.salutation) ? { value: userFormData.salutation, label: userFormData.salutation } : null}
                                        onChange={(opt) => handleUserInputChange({ target: { name: "salutation", value: opt?.value || "" } })}
                                        options={salutationOptions.map(opt => ({ value: opt, label: opt || 'Select' }))}
                                        isDisabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.suffix') || 'Suffix'}</Label>
                                    <Select
                                        activeColor={activeColor}
                                        className="react-select"
                                        styles={selectStyles}
                                        name="suffix"
                                        value={suffixOptions.find(opt => opt === userFormData.suffix) ? { value: userFormData.suffix, label: userFormData.suffix } : null}
                                        onChange={(opt) => handleUserInputChange({ target: { name: "suffix", value: opt?.value || "" } })}
                                        options={suffixOptions.map(opt => ({ value: opt, label: opt || 'Select' }))}
                                        isDisabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.gender') || 'Gender'}</Label>
                                    <Select
                                        activeColor={activeColor}
                                        className="react-select"
                                        styles={selectStyles}
                                        name="gender"
                                        value={genderOptions.find(opt => opt === userFormData.gender) ? { value: userFormData.gender, label: userFormData.gender } : null}
                                        onChange={(opt) => handleUserInputChange({ target: { name: "gender", value: opt?.value || "" } })}
                                        options={genderOptions.map(opt => ({ value: opt, label: opt || 'Select' }))}
                                        isDisabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.workPhone') || 'Work Phone'} *</Label>
                                    <Input
                                        className={inputClassName(!!userErrors.work_phone)}
                                        style={inputStyle}
                                        name="work_phone"
                                        value={userFormData.work_phone}
                                        onChange={handleUserInputChange}
                                        invalid={!!userErrors.work_phone}
                                        placeholder={t('userAdd.placeholders.workPhone')}
                                        disabled={createUserMutation.isPending}
                                    />
                                    {userErrors.work_phone && <div className="text-danger small">{userErrors.work_phone}</div>}
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.homePhone') || 'Home Phone'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="home_phone"
                                        value={userFormData.home_phone}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.homePhone')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="4">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.cellPhone') || 'Cell Phone'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="cell_phone"
                                        value={userFormData.cell_phone}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.cellPhone')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.fax') || 'Fax'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="fax"
                                        value={userFormData.fax}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.fax')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                        <Col md="6">
                            <div className="mb-1">
                                <FormGroup>
                                    <Label>{t('userAdd.labels.altEmail') || 'Alternative Email'}</Label>
                                    <Input
                                        className={inputClassName(false)}
                                        style={inputStyle}
                                        name="alt_email"
                                        value={userFormData.alt_email}
                                        onChange={handleUserInputChange}
                                        placeholder={t('userAdd.placeholders.altEmail')}
                                        disabled={createUserMutation.isPending}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <div className="mr-auto">
                            <Button
                                color="secondary"
                                outline
                                onClick={handlePrevStep}
                                disabled={createUserMutation.isPending}
                            >
                                Previous Step
                            </Button>
                        </div>
                        <Button
                            type="submit"
                            style={{ ...activeColorStyle, color: 'white' }}
                            disabled={createUserMutation.isPending}
                        >
                            {createUserMutation.isPending ? 'Saving...' : 'Save & Continue'}
                        </Button>
                        {tenantData?.step > 1 && (
                            <Button
                                color="secondary"
                                onClick={handleNextStep}
                                disabled={createUserMutation.isPending}
                            >
                                Next Step
                            </Button>
                        )}
                    </div>
                </Form>
            );
        }
        if (currentStep === 2) {
            return (
                <Institution activeColor={activeColor} tenantData={tenantData} onNext={handleNextStep} onPrev={handlePrevStep} showNext={true} {...theme2FormProps} />
            );
        }

        if (currentStep === 3) {
            return (
                <div className="py-2">
                    <SalesLocationsTab activeColor={activeColor} activeColorStyle={activeColorStyle} tenantData={tenantData} onNext={handleNextStep} onPrev={handlePrevStep} showNext={tenantData?.step > 3} {...theme2FormProps} />
                </div>
            );
        }

        if (currentStep === 4) {
            return (
                <div className="py-2">
                    <BookkeepingTab activeColor={activeColor} activeColorStyle={activeColorStyle} tenantData={tenantData} onNext={handleNextStep} onPrev={handlePrevStep} showNext={tenantData?.step > 4} {...theme2FormProps} />
                </div>
            );
        }

        if (currentStep === 5) {
            return (
                <div className="py-2">
                    <InvestorTab activeColor={activeColor} activeColorStyle={activeColorStyle} tenantData={tenantData} onNext={handleNextStep} onPrev={handlePrevStep} showNext={tenantData?.step > 5} {...theme2FormProps} />
                </div>
            );
        }

        if (currentStep === 6) {
            return (
                <div className="py-2">
                    <PortfolioTab activeColor={activeColor} activeColorStyle={activeColorStyle} tenantData={tenantData} onNext={() => navigate('/tenant')} onPrev={handlePrevStep} {...theme2FormProps} />
                </div>
            );
        }

        return (
            <div className="text-center py-5">
                <h4>Complete</h4>
                <p>The process is finished.</p>
                <Button color="success" onClick={() => navigate('/tenant')}>
                    Back to List
                </Button>
            </div>
        );
    };

    if (id && isLoadingTenant) {
        return (
            <div className={contentClassName}>
                <Theme2FormFieldsStyle css={tenantAddTheme2FieldsCss} />
                <div className="text-center">
                    <Spinner color="primary" />
                </div>
            </div>
        );
    }

    return (
        <div className={contentClassName}>
            <Theme2FormFieldsStyle css={tenantAddTheme2FieldsCss} />
            {!hideStepper && (
                <Stepper
                    currentStep={currentStep}
                    steps={steps}
                    activeColorStyle={activeColorStyle}
                />
            )}
            <Row>
                <Col md="12">
                    <Card className={classNames({ "tenant-add-institution-card": currentStep === 2 })}>
                        <CardBody className={formFieldsClassName}>
                            {renderStepContent()}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default TenantAdd;
